/**
 * Wizard step `experience` (§16.1 step 6, §5.3 Presentation, §13.3): theme tokens with a live WCAG contrast
 * check and the kit's fallback report (`buildTheme`, A11Y-01), hero media via storage presign (prizes agent's
 * asset flow) or a reference, energy level (D14), sound default (always off), reveal style, sponsor placements
 * and localized copy. Binds the `presentation` slice; autosave and validation belong to the frame.
 */
import {
  CRITICAL_CONTRAST_PAIRS,
  SponsorPlacement,
  contrastRatio,
  type ThemeTokens,
} from '@windrop/contracts';
import { Button, Upload, buildTheme, type UploadState } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  FormErrorSummary,
  RhfCheckbox,
  RhfSelect,
  RhfText,
  RhfTextarea,
  fieldId,
} from '../../../components/FormField';
import { ToneBadge } from '../../../components/StatusBadge';
import { lotteriesOpsApi } from '../../../lib/api/admin';
import { isApiError } from '../../../lib/api/client';
import { useWizard } from './WizardFrame';
import { stepForPath, type DraftConfigInput } from './steps';

const TOKENS: { key: keyof ThemeTokens; label: string }[] = [
  { key: 'canvas', label: 'Canvas' },
  { key: 'surface', label: 'Surface' },
  { key: 'textPrimary', label: 'Text primary' },
  { key: 'textSecondary', label: 'Text secondary' },
  { key: 'border', label: 'Border' },
  { key: 'action', label: 'Action (CTA)' },
  { key: 'actionText', label: 'Text on action' },
  { key: 'focus', label: 'Focus ring' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'error', label: 'Error' },
  { key: 'sponsorAccent', label: 'Sponsor accent' },
  { key: 'prizeAccent', label: 'Prize accent' },
];

const HEX = /^#[0-9a-fA-F]{6}$/;

export function ExperienceStep() {
  const { control, setValue } = useFormContext<DraftConfigInput>();
  const { issues, goToStep } = useWizard();
  const theme = useWatch({ control, name: 'presentation.theme' }) as Partial<ThemeTokens> | undefined;
  const locales = (useWatch({ control, name: 'identity.locales' }) as string[] | undefined) ?? ['en'];
  const heroRef = useWatch({ control, name: 'presentation.heroMedia.ref' }) as string | undefined;
  const mine = issues.filter((i) => i.step === 'experience');

  const contrast = useMemo(() => {
    if (!theme) return [];
    return CRITICAL_CONTRAST_PAIRS.map((pair) => {
      const fg = theme[pair.fg];
      const bg = theme[pair.bg];
      const ratio = fg && bg && HEX.test(fg) && HEX.test(bg) ? contrastRatio(fg, bg) : 0;
      return { ...pair, ratio, ok: ratio >= pair.min };
    });
  }, [theme]);
  const kit = useMemo(
    () =>
      theme?.action && theme.prizeAccent && theme.sponsorAccent && HEX.test(theme.action)
        ? buildTheme({
            organizerAccent: theme.action,
            prizeAccent: theme.prizeAccent,
            sponsorAccent: theme.sponsorAccent,
            mode:
              theme.canvas && HEX.test(theme.canvas) && contrastRatio(theme.canvas, '#ffffff') < 3
                ? 'light'
                : 'dark',
          })
        : null,
    [theme],
  );
  const failing = contrast.filter((c) => !c.ok);

  return (
    <div className="wd-stack" data-testid="experience-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in experience`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />

      <h2>Theme tokens (§13.3)</h2>
      <p className="wd-muted">
        Critical pairs must reach WCAG AA (text 4.5:1, essential UI 3:1); publish cannot bypass them (Appendix
        A &ldquo;sponsor asset fails contrast&rdquo;).
      </p>
      <div className="wdc-form-grid">
        {TOKENS.map((t) => (
          <div key={t.key} className="wdc-actions-row">
            <RhfText
              control={control}
              name={`presentation.theme.${t.key}`}
              label={t.label}
              required
              placeholder="#rrggbb"
              parse={(raw) => raw.trim()}
            />
            <span
              aria-hidden
              className="wdc-token-swatch"
              style={{
                background:
                  theme?.[t.key] && HEX.test(theme[t.key] as string)
                    ? (theme[t.key] as string)
                    : 'transparent',
              }}
            />
          </div>
        ))}
      </div>
      <section className="wdc-panel" data-testid="contrast-report" aria-live="polite">
        <h3>Contrast check</h3>
        {failing.length === 0 ? (
          <ToneBadge tone="success">All critical pairs pass</ToneBadge>
        ) : (
          <ToneBadge tone="error">
            {failing.length} pair{failing.length === 1 ? '' : 's'} below the minimum
          </ToneBadge>
        )}
        <ul>
          {contrast.map((c) => (
            <li key={`${c.fg}-${c.bg}`}>
              <a href={`#${fieldId(`presentation.theme.${c.fg}`)}`}>{c.fg}</a> on {c.bg}: {c.ratio.toFixed(2)}
              :1 (min {c.min}:1) {c.ok ? '✓' : '✗'}
            </li>
          ))}
        </ul>
        {kit ? (
          <p className="wd-muted" data-testid="fallback-report">
            Kit fallback report ({kit.mode} mode):{' '}
            {kit.fallbacks.length === 0
              ? 'every brand colour maps to its role.'
              : kit.fallbacks.map((f) => `${f.role} → neutral (${f.reason})`).join('; ')}
          </p>
        ) : null}
      </section>

      <h2>Hero media</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="presentation.heroMedia.kind"
          label="Kind"
          required
          options={[
            { value: 'IMAGE', label: 'Image' },
            { value: 'VIDEO', label: 'Video (poster required; never autoplay)' },
          ]}
        />
        <RhfText
          control={control}
          name="presentation.heroMedia.ref"
          label="Media reference"
          required
          hint="Storage key (asset:… from an upload), a URL, or prize:<prizeId> to reuse the prize hero image."
        />
        <RhfText
          control={control}
          name="presentation.heroMedia.altText"
          label="Alt text"
          required
          hint="Describes the prize for screen readers (§13.5)."
        />
        <RhfText control={control} name="presentation.heroMedia.posterRef" label="Poster reference (video)" />
        <RhfText
          control={control}
          name="presentation.heroMedia.width"
          label="Width (px)"
          type="number"
          parse={(raw) => (raw === '' ? undefined : Number(raw))}
          format={(v) => (v == null ? '' : String(v))}
        />
        <RhfText
          control={control}
          name="presentation.heroMedia.height"
          label="Height (px)"
          type="number"
          parse={(raw) => (raw === '' ? undefined : Number(raw))}
          format={(v) => (v == null ? '' : String(v))}
        />
      </div>
      <HeroUpload
        onUploaded={(ref, alt, width, height) => {
          setValue('presentation.heroMedia.ref', ref, { shouldDirty: true, shouldValidate: true });
          if (alt) setValue('presentation.heroMedia.altText', alt, { shouldDirty: true });
          if (width) setValue('presentation.heroMedia.width', width, { shouldDirty: true });
          if (height) setValue('presentation.heroMedia.height', height, { shouldDirty: true });
        }}
        currentRef={heroRef}
      />

      <h2>Energy, sound, reveal (D14, §13.2)</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="presentation.energyLevel"
          label="Energy level"
          required
          options={[
            { value: 'FULL', label: 'Full game-show energy (calm mode always available)' },
            { value: 'CALM', label: 'Calm (static equivalent for everyone)' },
          ]}
        />
        <RhfSelect
          control={control}
          name="presentation.revealStyle"
          label="Reveal style"
          required
          options={['DRAW_ROOM', 'WHEEL', 'SCRATCH', 'INSTANT_CARD', 'MILESTONE_COUNTER', 'NUMBER_BOARD'].map(
            (v) => ({ value: v, label: v.toLowerCase().replace(/_/g, ' ') }),
          )}
        />
        <div className="wd-field">
          <span className="wd-field__label">Sound default</span>
          <p className="wd-muted" id={fieldId('presentation.soundDefault')}>
            Off (fixed, §5.3). Participants opt in per device.
          </p>
        </div>
      </div>

      <h2>Sponsor placements (§7.1)</h2>
      <div className="wdc-form-grid" id={fieldId('presentation.sponsorPlacements')}>
        {SponsorPlacement.options.map((p) => (
          <PlacementToggle key={p} placement={p} />
        ))}
      </div>

      <h2>Localized copy (§15.4)</h2>
      {locales.map((locale) => (
        <fieldset key={locale} className="wd-stack wdc-fieldset">
          <legend>{locale}</legend>
          <div className="wdc-form-grid">
            <RhfText control={control} name={`presentation.content.${locale}.title`} label="Title" required />
            <RhfText
              control={control}
              name={`presentation.content.${locale}.prizeStatement`}
              label="Prize statement"
              required
              hint="Concise; the primary CTA never appears without cost, eligibility and timing."
            />
          </div>
          <RhfTextarea
            control={control}
            name={`presentation.content.${locale}.howSelectionWorks`}
            label="How selection works"
            rows={3}
          />
          <RhfTextarea
            control={control}
            name={`presentation.content.${locale}.sponsorStory`}
            label="Sponsor story"
            rows={3}
          />
          <RhfText
            control={control}
            name={`presentation.content.${locale}.rulesRef`}
            label="Full rules reference (optional)"
          />
          <RhfText
            control={control}
            name={`presentation.content.${locale}.privacyRef`}
            label="Privacy reference (optional)"
          />
        </fieldset>
      ))}
      <RhfCheckbox
        control={control}
        name="presentation.soundDefault"
        label="Sound default (always off)"
        disabled
      />
    </div>
  );
}

function PlacementToggle({ placement }: { placement: SponsorPlacement }) {
  const { control, setValue } = useFormContext<DraftConfigInput>();
  const list =
    (useWatch({ control, name: 'presentation.sponsorPlacements' }) as SponsorPlacement[] | undefined) ?? [];
  const on = list.includes(placement);
  return (
    <label className="wdc-check-list__item">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) =>
          setValue(
            'presentation.sponsorPlacements',
            e.target.checked ? [...list, placement] : list.filter((p) => p !== placement),
            { shouldDirty: true },
          )
        }
      />
      <span>{placement.toLowerCase().replace(/_/g, ' ')}</span>
    </label>
  );
}

function HeroUpload({
  onUploaded,
  currentRef,
}: {
  onUploaded: (ref: string, alt?: string, width?: number, height?: number) => void;
  currentRef?: string;
}) {
  const [state, setState] = useState<UploadState>('idle');
  const [reason, setReason] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [alt, setAlt] = useState('');

  const onFile = async (file: File) => {
    setFileName(file.name);
    setState('uploading');
    setReason(undefined);
    try {
      const presigned = await lotteriesOpsApi.presignAsset({
        purpose: 'PRIZE_IMAGE',
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const put = await fetch(presigned.upload.url, {
        method: 'PUT',
        headers: { ...presigned.upload.headers, 'content-type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      setState('scanning');
      const asset = await lotteriesOpsApi.finalizeAsset(presigned.assetId, {
        alt: alt || file.name,
        rightsConfirmed: true,
      });
      setState('clean');
      onUploaded(asset.ref, asset.alt, asset.width, asset.height);
    } catch (e) {
      setState('failed');
      setReason(isApiError(e) ? e.message : e instanceof Error ? e.message : 'The upload did not complete.');
    }
  };

  return (
    <div className="wd-stack wd-surface" style={{ padding: 'var(--wd-space-3)' }} data-testid="hero-upload">
      <p className="wd-muted">
        Upload through the catalogue asset flow (signed PUT, type/size/dimension checks, scan). Rights must be
        confirmed; alt text is required.
      </p>
      <label className="wd-field">
        <span className="wd-field__label">Alt text for the upload</span>
        <input className="wd-input" value={alt} onChange={(e) => setAlt(e.target.value)} />
      </label>
      <Upload
        label="Hero image"
        accept={['image/jpeg', 'image/png', 'image/webp', 'image/avif']}
        maxBytes={5 * 1024 * 1024}
        state={state}
        reason={reason}
        fileName={fileName}
        onFile={(f) => void onFile(f)}
        onRemove={() => setState('idle')}
        hint={currentRef ? `Current reference: ${currentRef}` : undefined}
      />
      {state === 'failed' ? (
        <Button variant="quiet" onClick={() => setState('idle')}>
          Try another file
        </Button>
      ) : null}
    </div>
  );
}
